import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { get, post, put, del } from '../../services/api';
import styles from './TimetableEntryModal.module.css';

const TimetableEntryModal = ({ mode, entry, cell, configuration, onSave, onClose }) => {
  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm();
  
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const headers = { Authorization: `Bearer ${token}` };

  const selectedClassWatch = watch('classId');
  const selectedSectionWatch = watch('classSectionId');

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    
    if (mode === 'edit' && entry) {
      // Populate form for editing
      setValue('classId', { value: entry.classSectionId, label: `${entry.className} - ${entry.sectionName}` });
      setValue('classSectionId', { value: entry.classSectionId, label: entry.sectionName });
      setValue('subjectId', { value: entry.subjectId, label: entry.subjectName });
      setValue('teacherId', { value: entry.teacherId, label: entry.teacherName });
      setValue('dayOfWeek', { value: entry.dayOfWeek, label: entry.dayName });
      setValue('periodNumber', { value: entry.periodNumber, label: `Period ${entry.periodNumber}` });
      setValue('roomNumber', entry.roomNumber || '');
    } else if (mode === 'create' && cell) {
      // Pre-fill day and period for new entry
      const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][cell.dayOfWeek - 1];
      setValue('dayOfWeek', { value: cell.dayOfWeek, label: dayName });
      setValue('periodNumber', { value: cell.periodNumber, label: `Period ${cell.periodNumber}` });
    }
  }, [mode, entry, cell]);

  useEffect(() => {
    if (selectedClassWatch) {
      fetchSections(selectedClassWatch.value);
    }
  }, [selectedClassWatch]);

  useEffect(() => {
    if (selectedSectionWatch) {
      fetchSubjects(selectedSectionWatch.value);
    }
  }, [selectedSectionWatch]);

  const fetchClasses = async () => {
    try {
      const response = await get(`CenterClass/GetClassCenterByCenterId?id=${centerId}`, { headers });
      setClassesData(response || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await get(
        `ClassSection/GetAllSectionByCenterClassIdAndCenterIds?centerClassId=${classId}&centerId=${centerId}`,
        { headers }
      );
      setSectionsData(response || []);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const fetchSubjects = async (classSectionId) => {
    try {
      const response = await get(
        `StudentEvaluation/GetSubjectByClassSectionId?ClassSectionId=${classSectionId}`,
        { headers }
      );
      setSubjectsData(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await get(
        `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${centerId}&roleId=7`,
        { headers }
      );
      setTeachersData(response || []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      const entryData = {
        configurationId: configuration.id,
        classSectionId: data.classSectionId.value,
        subjectId: data.subjectId.value,
        teacherId: data.teacherId.value,
        dayOfWeek: data.dayOfWeek.value,
        periodNumber: data.periodNumber.value,
        roomNumber: data.roomNumber || ''
      };

      await onSave(entryData);
      toast.success(mode === 'create' ? 'Entry added successfully!' : 'Entry updated successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Operation failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const classOptions = classesData.map(c => ({ value: c.id, label: c.name }));
  const sectionOptions = sectionsData.map(s => ({ value: s.id, label: s.name }));
  const subjectOptions = subjectsData.map(s => ({ value: s.id, label: s.name }));
  const teacherOptions = teachersData.map(t => ({ value: t.id, label: t.firstName + t.lastName }));

  const dayOptions = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' }
  ].slice(0, configuration?.daysPerWeek || 6);

  const periodOptions = configuration?.periods
    ?.filter(p => !p.isBreak)
    ?.map(p => ({
      value: p.periodNumber,
      label: `Period ${p.periodNumber} (${p.startTime} - ${p.endTime})`
    })) || [];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{mode === 'create' ? 'Add Timetable Entry' : 'Edit Timetable Entry'}</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.modalForm}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Class *</label>
              <Controller
                name="classId"
                control={control}
                rules={{ required: 'Class is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={classOptions}
                    placeholder="Select Class"
                    isDisabled={mode === 'edit'}
                  />
                )}
              />
              {errors.classId && <span className={styles.error}>{errors.classId.message}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Section *</label>
              <Controller
                name="classSectionId"
                control={control}
                rules={{ required: 'Section is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={sectionOptions}
                    placeholder="Select Section"
                    isDisabled={!selectedClassWatch || mode === 'edit'}
                  />
                )}
              />
              {errors.classSectionId && <span className={styles.error}>{errors.classSectionId.message}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Subject *</label>
              <Controller
                name="subjectId"
                control={control}
                rules={{ required: 'Subject is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={subjectOptions}
                    placeholder="Select Subject"
                    isDisabled={!selectedSectionWatch}
                  />
                )}
              />
              {errors.subjectId && <span className={styles.error}>{errors.subjectId.message}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Teacher *</label>
              <Controller
                name="teacherId"
                control={control}
                rules={{ required: 'Teacher is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={teacherOptions}
                    placeholder="Select Teacher"
                  />
                )}
              />
              {errors.teacherId && <span className={styles.error}>{errors.teacherId.message}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Day *</label>
              <Controller
                name="dayOfWeek"
                control={control}
                rules={{ required: 'Day is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={dayOptions}
                    placeholder="Select Day"
                    isDisabled={mode === 'create' && cell}
                  />
                )}
              />
              {errors.dayOfWeek && <span className={styles.error}>{errors.dayOfWeek.message}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Period *</label>
              <Controller
                name="periodNumber"
                control={control}
                rules={{ required: 'Period is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={periodOptions}
                    placeholder="Select Period"
                    isDisabled={mode === 'create' && cell}
                  />
                )}
              />
              {errors.periodNumber && <span className={styles.error}>{errors.periodNumber.message}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Room Number</label>
              <Controller
                name="roomNumber"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" placeholder="e.g., Room 101" />
                )}
              />
            </div>
          </div>

          <div className={styles.modalActions}>
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={loading} className={styles.saveButton}>
              {loading ? 'Saving...' : mode === 'create' ? 'Add Entry' : 'Update Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimetableEntryModal;